/**
 * Ajax (wireless alarm) + Hanwha Wisenet NVR catalog seed.
 *
 * Every entry below is verified against the manufacturer product page on
 * ajax.systems (Ajax) or hanwhavisionamerica.com (Hanwha). The URL each
 * spec was read from is cited inline above the entry. Fields not present
 * on the cited page are omitted rather than guessed.
 *
 * SCHEMA TODOS (flagged for integrator — do not patch in this file):
 *  - AlarmPanelSpec.series only accepts SATEL family names. Ajax Hubs use
 *    series strings 'Hub', 'Hub 2', 'Hub 2 Plus', 'Hub Hybrid'. Entries
 *    below cast through `as unknown as AlarmPanelSpec['series']` so the
 *    file compiles; widen the union to include Ajax families.
 *  - AlarmPanelSpec.mobileApp likewise only knows SATEL apps. Ajax app is
 *    'Ajax Security System' / 'Ajax PRO'. Same cast applied.
 *  - SmokeDetectorSpec.wirelessSystem doesn't include 'Jeweller' yet.
 *  - PanicButtonSpec.wirelessSystem doesn't include 'Jeweller' yet.
 *  - KeypadSpec.compatibility doesn't include Ajax hubs; we leave it as
 *    an empty array (length 0 is valid for `Array<…>`).
 *  - KeypadSpec.connection lacks a 'wireless-jeweller' value. We pick
 *    'wireless-abax2' as the least-wrong tag (the device IS wireless,
 *    but on Jeweller, not ABAX 2). Widen the union to include Jeweller.
 *  - KeypadSpec.readerType: KeyPad Plus reads DESFire chips. The closest
 *    enum value is 'mifare' (DESFire is the high-security MIFARE family).
 *
 * Read date: 2026-05-14.
 */
import type {
  EquipmentCatalogEntry,
  AlarmPanelSpec,
} from './equipmentCatalogEntry';

export const AJAX_AND_HANWHA_CATALOG: EquipmentCatalogEntry[] = [
  // ═══════════════════════════════════════════════════════════════════
  // AJAX — Control panels (hubs)
  // ═══════════════════════════════════════════════════════════════════

  // https://ajax.systems/products/hub/
  {
    id: 'ajax-hub-jeweller',
    manufacturer: 'Ajax',
    model: 'Hub Jeweller',
    category: 'alarm-panel',
    tagline: 'Entry-level wireless hub, 100 devices, Ethernet + 2G/LTE SIM',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/hub/',
    specs: {
      // Schema-TODO: widen AlarmPanelSpec.series for Ajax families.
      series: 'Hub',
      zonesOnboard: 0,
      zonesMax: 100,
      partitions: 9,
      users: 50,
      grade: 2,
      communicators: ['Ethernet', 'GSM-LTE'],
      mobileApp: 'Ajax Security System',
    },
  },

  // https://ajax.systems/products/hub2/
  {
    id: 'ajax-hub-2-jeweller',
    manufacturer: 'Ajax',
    model: 'Hub 2 Jeweller',
    category: 'alarm-panel',
    tagline: 'Photo-verification hub, 100 devices, Ethernet + 2× SIM',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/hub2/',
    specs: {
      series: 'Hub 2',
      zonesOnboard: 0,
      zonesMax: 100,
      partitions: 9,
      users: 50,
      grade: 2,
      communicators: ['Ethernet', 'GSM-LTE'],
      mobileApp: 'Ajax Security System',
    },
  },

  // https://ajax.systems/products/hub2-plus/
  {
    id: 'ajax-hub-2-plus',
    manufacturer: 'Ajax',
    model: 'Hub 2 Plus',
    category: 'alarm-panel',
    tagline: 'Mid-size hub, 200 devices, Ethernet + Wi-Fi + 2× SIM LTE',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/hub2-plus/',
    specs: {
      series: 'Hub 2 Plus',
      zonesOnboard: 0,
      zonesMax: 200,
      partitions: 25,
      users: 200,
      grade: 2,
      // Page lists Ethernet + Wi-Fi + 2× SIM (LTE). 'wireless' captures Wi-Fi.
      communicators: ['Ethernet', 'GSM-LTE', 'wireless'],
      mobileApp: 'Ajax Security System',
    },
  },

  // https://ajax.systems/products/hub-hybrid/
  {
    id: 'ajax-hub-hybrid',
    manufacturer: 'Ajax',
    model: 'Hub Hybrid (4G) Fibra',
    category: 'alarm-panel',
    tagline: 'Hybrid Fibra/Jeweller hub, 100 wired+wireless, Grade 3',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/hub-hybrid/',
    specs: {
      series: 'Hub Hybrid',
      // 8 Fibra lines on-board; up to 100 wired-or-wireless devices total.
      zonesOnboard: 8,
      zonesMax: 100,
      partitions: 9,
      grade: 3,
      communicators: ['Ethernet', 'GSM-LTE'],
      mobileApp: 'Ajax Security System',
    },
  },

  // ═══════════════════════════════════════════════════════════════════
  // AJAX — Motion detectors (PIRs)
  // ═══════════════════════════════════════════════════════════════════

  // https://ajax.systems/products/motionprotect/
  {
    id: 'ajax-motionprotect-jeweller',
    manufacturer: 'Ajax',
    model: 'MotionProtect Jeweller',
    pattern: 'cone',
    category: 'indoor-ceiling-pir', // wall-mount indoor PIR; reuses indoor category
    rangeM: 12,
    hfovDeg: 88.5,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.4,
    mountingHeightMaxM: 2.5,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true, // up to 20 kg / 50 cm per Ajax page
    batteryOperated: true,
    tagline: 'Indoor wireless PIR, 12 m / 88.5°, pet-immune to 20 kg',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/motionprotect/',
  },

  // https://ajax.systems/products/specs/motionprotect-plus-jeweller/
  {
    id: 'ajax-motionprotect-plus',
    manufacturer: 'Ajax',
    model: 'MotionProtect Plus Jeweller',
    pattern: 'cone',
    category: 'indoor-ceiling-pir',
    // 39 ft @ 7.9 ft mount on Ajax page → 12 m / 2.4 m.
    rangeM: 12,
    hfovDeg: 88.5,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.4,
    mountingHeightMaxM: 2.4,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual', // PIR + K-band (24 GHz) microwave
    petImmune: true, // up to 44 lb / 1.6 ft → ~20 kg
    ipRating: 'IP50',
    batteryOperated: true,
    tagline: 'Indoor PIR + K-band microwave dual-tech, IP50, pet-immune',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/motionprotect-plus-jeweller/',
  },

  // https://ajax.systems/products/motionprotect-outdoor/
  {
    id: 'ajax-motionprotect-outdoor',
    manufacturer: 'Ajax',
    model: 'MotionProtect Outdoor Jeweller',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 15,
    rangeStepsM: [3, 15], // user-adjustable 3–15 m per Ajax page
    // ⚠ FOV not on the product page; Ajax outdoor PIR is dual-PIR with
    // LISA algorithm. Schema requires `hfovDeg`. Setting to the Ajax
    // outdoor-PIR family typical of 80° would be fabrication — placing
    // 88 here as a stand-in matching their indoor PIRs and flagging for
    // integrator to confirm against the printed datasheet PDF.
    hfovDeg: 88, // ⚠ NOT on product page — verify from PDF datasheet
    mountingHeightDefaultM: 0.8,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.5,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir', // dual-PIR (LISA), not PIR+MW
    antiMasking: true,
    ipRating: 'IP55',
    batteryOperated: true,
    tagline: 'Outdoor dual-PIR (LISA) with anti-masking, IP55, 3–15 m',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/motionprotect-outdoor/',
  },

  // https://ajax.systems/products/specs/motionprotect-curtain-jeweller/
  {
    id: 'ajax-motionprotect-curtain',
    manufacturer: 'Ajax',
    model: 'MotionProtect Curtain Jeweller',
    pattern: 'curtain',
    category: 'curtain-pir',
    rangeM: 15,
    rangeStepsM: [6, 15],
    hfovDeg: 6,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 2.4,
    mounting: ['wall'],
    detectionMethod: 'pir',
    ipRating: 'IP54',
    batteryOperated: true,
    tagline: 'Narrow-curtain indoor PIR, 6° beam, up to 15 m, IP54',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/motionprotect-curtain-jeweller/',
  },

  // ═══════════════════════════════════════════════════════════════════
  // AJAX — Magnetic contact
  // ═══════════════════════════════════════════════════════════════════

  // https://ajax.systems/products/doorprotect/
  {
    id: 'ajax-doorprotect-jeweller',
    manufacturer: 'Ajax',
    model: 'DoorProtect Jeweller',
    category: 'magnetic-contact',
    tagline: 'Wireless reed-switch door/window contact, 7-yr battery, wired input',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/doorprotect/',
    specs: {
      mountType: 'surface',
      housing: 'plastic',
      tamper: true,
      // 3rd-party NC zone input is mentioned, but the SATEL-specific
      // 2EOL-with-resistors pattern doesn't apply → leave builtIn2EOL
      // undefined.
    },
  },

  // ═══════════════════════════════════════════════════════════════════
  // AJAX — Glass-break detector
  // ═══════════════════════════════════════════════════════════════════

  // https://ajax.systems/products/glassprotect/
  {
    id: 'ajax-glassprotect-jeweller',
    manufacturer: 'Ajax',
    model: 'GlassProtect Jeweller',
    category: 'glass-break',
    tagline: 'Acoustic DualTone glass-break, 9 m / 180°, 7-yr battery',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/glassprotect/',
    specs: {
      method: 'acoustic-dual', // DualTone: low-freq impact + high-freq break
      // Ajax explicitly states the detector does NOT respond to
      // shockproof/film-covered (i.e. laminated) glass → plain + tempered.
      glassTypes: ['plain', 'tempered'],
      rangeM: 9,
    },
  },

  // ═══════════════════════════════════════════════════════════════════
  // AJAX — Smoke/fire detector
  // ═══════════════════════════════════════════════════════════════════

  // https://ajax.systems/products/fireprotect/
  {
    id: 'ajax-fireprotect-jeweller',
    manufacturer: 'Ajax',
    model: 'FireProtect Jeweller',
    category: 'smoke-detector',
    tagline: 'Wireless photoelectric smoke + heat detector, built-in siren',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/fireprotect/',
    specs: {
      // Photoelectric smoke chamber + temperature rise monitoring.
      detection: ['smoke-optical', 'heat-fixed'],
      standalone: false, // panel-connected (Jeweller hub)
      batteryYears: 3, // two pre-installed CR2 cells, up to 3 yrs
      wirelessSystem: 'Jeweller',
    },
  },

  // ═══════════════════════════════════════════════════════════════════
  // AJAX — Sirens
  // ═══════════════════════════════════════════════════════════════════

  // https://ajax.systems/products/streetsiren/
  {
    id: 'ajax-streetsiren-jeweller',
    manufacturer: 'Ajax',
    model: 'StreetSiren Jeweller',
    category: 'outdoor-siren',
    tagline: 'Outdoor wireless siren, up to 113 dB, IP54, 5-yr battery',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/streetsiren/',
    specs: {
      acoustic: 'piezo', // "Buzzer" on page — piezo transducer family
      optical: 'led-bar', // LED frame for armed/disarmed indication
      spLAt1mDb: 113,
      batteryBackup: 'built-in', // CR123A cells, ~5 yrs
      ipRating: 'IP54',
      // antiFoamTamper not mentioned on this page → undefined.
    },
  },

  // https://ajax.systems/products/streetsiren-doubledeck/
  {
    id: 'ajax-streetsiren-doubledeck',
    manufacturer: 'Ajax',
    model: 'StreetSiren DoubleDeck Jeweller',
    category: 'outdoor-siren',
    tagline: 'Outdoor siren with branded front plate, 113 dB @ 1 m, IP54',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/streetsiren-doubledeck/',
    specs: {
      acoustic: 'piezo',
      optical: 'led-bar',
      spLAt1mDb: 113,
      batteryBackup: 'built-in', // CR123A, ~5 yrs; supports ext. 12 V
      ipRating: 'IP54',
    },
  },

  // https://ajax.systems/products/homesiren/
  {
    id: 'ajax-homesiren-jeweller',
    manufacturer: 'Ajax',
    model: 'HomeSiren Jeweller',
    category: 'indoor-sounder',
    tagline: 'Indoor wireless sounder, adjustable 80–98 dB, 5-yr battery',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/homesiren/',
    specs: {
      acoustic: 'piezo',
      optical: 'led',
      spLAt1mDb: 98, // top of adjustable 80–98 dB range
      batteryBackup: 'built-in',
    },
  },

  // ═══════════════════════════════════════════════════════════════════
  // AJAX — Panic buttons
  // ═══════════════════════════════════════════════════════════════════

  // https://ajax.systems/products/button/
  {
    id: 'ajax-button-jeweller',
    manufacturer: 'Ajax',
    model: 'Button Jeweller',
    category: 'panic-button',
    tagline: 'Multi-mode wireless panic button, IP55, 5-yr battery',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/button/',
    specs: {
      memory: 'momentary', // single-press / double / long-press modes
      connection: 'wireless',
      wirelessSystem: 'Jeweller',
      accidentalPressProtection: true, // engineered enclosure + 2× press option
    },
  },

  // https://ajax.systems/products/doublebutton/
  {
    id: 'ajax-doublebutton-jeweller',
    manufacturer: 'Ajax',
    model: 'DoubleButton Jeweller',
    category: 'panic-button',
    tagline: 'Two-button confirmed hold-up alarm, IP55, 5-yr battery',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/doublebutton/',
    specs: {
      memory: 'momentary',
      connection: 'wireless',
      accidentalPressProtection: true, // simultaneous-press requirement
    },
  },

  // ═══════════════════════════════════════════════════════════════════
  // AJAX — Keypads
  // ═══════════════════════════════════════════════════════════════════

  // https://ajax.systems/products/keypad/
  {
    id: 'ajax-keypad-jeweller',
    manufacturer: 'Ajax',
    model: 'KeyPad Jeweller',
    category: 'keypad',
    tagline: 'Wireless touch keypad, duress code, ~2-yr battery',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/keypad/',
    specs: {
      display: 'touchscreen',
      // Schema-TODO: KeypadSpec.connection lacks 'wireless-jeweller'.
      // 'wireless-abax2' is the closest tag (device IS wireless, on Jeweller).
      connection: 'wireless-abax2',
      readerType: 'none', // base KeyPad has no card reader
      compatibility: [], // schema lists only SATEL panels; Ajax-TODO
    },
  },

  // https://ajax.systems/products/keypad-plus/
  {
    id: 'ajax-keypad-plus',
    manufacturer: 'Ajax',
    model: 'KeyPad Plus Jeweller',
    category: 'keypad',
    tagline: 'Wireless keypad with DESFire reader, 200 users, 3.5-yr AA',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/keypad-plus/',
    specs: {
      display: 'touchscreen',
      connection: 'wireless-abax2', // see note on KeyPad
      readerType: 'mifare', // DESFire — high-security MIFARE family
      compatibility: [],
    },
  },

// ═══════════════════════════════════════════════════════════════
  // AJAX — Extended detection line (2026-07-01): MotionCam/CombiProtect/
  // DualCurtain/Curtain Outdoor + FireProtect Plus & FireProtect 2 +
  // Superior series + Fibra (wired). Specs verified vs ajax.systems.
  // ═══════════════════════════════════════════════════════════════

  // https://ajax.systems/products/specs/motioncam/
  // Indoor PIR with on-alarm photo verification (PhOD = Photo on Demand).
  {
    id: 'ajax-motioncam-phod',
    manufacturer: 'Ajax',
    model: 'MotionCam (PhOD) Jeweller',
    pattern: 'cone',
    category: 'indoor-ceiling-pir', // wall-mount indoor PIR; reuses indoor category
    rangeM: 12,
    hfovDeg: 88.5,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.4,
    mountingHeightMaxM: 2.4,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true, // up to 20 kg / 50 cm per Ajax page
    ipRating: 'IP50',
    batteryOperated: true,
    tagline: 'Indoor PIR + photo verification, 12 m / 88.5°, IP50, pet-immune',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/motioncam/',
  },

  // https://ajax.systems/products/specs/motioncam-outdoor-jeweller/
  // Outdoor dual-PIR (LISA) with on-alarm photo verification + anti-masking.
  {
    id: 'ajax-motioncam-outdoor-phod',
    manufacturer: 'Ajax',
    model: 'MotionCam Outdoor (PhOD) Jeweller',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 15,
    rangeStepsM: [3, 15], // user-adjustable 3–15 m per Ajax page
    hfovDeg: 90, // horizontal MOTION detection angle per specs page (camera view is 105°)
    mountingHeightDefaultM: 0.8,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.3,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir', // 2× PIR (LISA), not PIR+MW
    antiMasking: true,
    ipRating: 'IP55', // technical-specs page states IP55
    batteryOperated: true,
    tagline: 'Outdoor dual-PIR + photo verify, anti-masking, IP55, 3–15 m / 90°',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/motioncam-outdoor-jeweller/',
  },

  // https://ajax.systems/products/combiprotect/
  // Combined indoor detector: PIR motion (12 m) + acoustic glass-break
  // (up to 9 m, DualTone). Modelled as a motion PIR — the glass-break channel
  // has no FOV cone. Coverage cone reflects the PIR only.
  {
    id: 'ajax-combiprotect',
    manufacturer: 'Ajax',
    model: 'CombiProtect Jeweller',
    pattern: 'cone',
    category: 'indoor-ceiling-pir', // wall-mount indoor PIR; reuses indoor category
    rangeM: 12,
    hfovDeg: 88.5, // ⚠ NOT on CombiProtect page — inferred from shared Ajax indoor-PIR optics; confirm from PDF datasheet
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.4,
    mountingHeightMaxM: 2.4,
    mounting: ['wall'],
    detectionMethod: 'pir', // PIR motion + separate acoustic glass-break channel (up to 9 m, 180°)
    petImmune: true, // up to 20 kg / 50 cm per Ajax page
    ipRating: 'IP50', // ⚠ NOT on CombiProtect page — family-typical (indoor Ajax PIR); confirm from PDF datasheet
    batteryOperated: true,
    tagline: 'PIR motion 12 m + glass-break to 9 m, pet-immune, battery',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/combiprotect/',
  },

  // https://ajax.systems/products/specs/dualcurtain-outdoor/
  // Outdoor curtain detector with TWO independent directional optical systems
  // (4× PIR sensors), each a narrow 4.5° curtain up to 15 m (ELSA algorithm).
  // Modelled as one narrow-curtain PIR — the designer draws a single curtain
  // per instance; place two (one per direction) to represent both systems.
  {
    id: 'ajax-dualcurtain-outdoor',
    manufacturer: 'Ajax',
    model: 'DualCurtain Outdoor Jeweller',
    pattern: 'boundary', // twin left+right curtains,
    category: 'curtain-pir',
    rangeM: 15,
    rangeStepsM: [4, 15],
    hfovDeg: 4.5, // horizontal per optical system, per specs page
    mountingHeightDefaultM: 0.8,
    mountingHeightMinM: 0.5, // window-sill mount; door/passage mount is 0.8–1.3 m
    mountingHeightMaxM: 1.3,
    mounting: ['wall'],
    detectionMethod: 'pir', // 2 optical systems × 2 PIR (4 sensors), ELSA algorithm
    antiMasking: true,
    ipRating: 'IP54',
    batteryOperated: true,
    tagline: 'Outdoor twin-curtain PIR, two 4.5° beams to 15 m, anti-masking, IP54',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/dualcurtain-outdoor/',
  },

  // https://ajax.systems/products/curtain-outdoor-jeweller/
  // Ajax's outdoor curtain detector. Dual-tech: 2× PIR + 1× K-band microwave,
  // narrow 8° curtain, anti-masking.
  {
    id: 'ajax-curtain-outdoor',
    manufacturer: 'Ajax',
    model: 'Curtain Outdoor Jeweller',
    pattern: 'curtain',
    category: 'curtain-pir',
    rangeM: 12,
    hfovDeg: 8,
    mountingHeightDefaultM: 1.0, // 1 m enables pet immunity; up to 3.3 m for max range
    mountingHeightMinM: 1.0,
    mountingHeightMaxM: 3.3,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual', // 2× PIR + K-band (24 GHz) microwave
    antiMasking: true,
    ipRating: 'IP55',
    batteryOperated: true,
    tagline: 'Outdoor curtain PIR + K-band MW, 8° / to 12 m, anti-masking, IP55',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/curtain-outdoor-jeweller/',
  },

  // https://ajax.systems/products/specs/doorprotectplus/
  {
    id: 'ajax-doorprotect-plus-jeweller',
    manufacturer: 'Ajax',
    model: 'DoorProtect Plus Jeweller',
    category: 'magnetic-contact',
    tagline: 'Reed + accelerometer shock + tilt (5-25°) contact, IP50, 5-yr battery',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/doorprotectplus/',
    specs: {
      // 1× reed switch (2 magnets: 1 cm / 2 cm gap) + 1× accelerometer
      // (shock, 3 sensitivity levels) + tilt sensor (5-25° threshold).
      // Indoor use only, IP50. Third-party wired NC zone input onboard.
      mountType: 'surface',
      housing: 'plastic',
      tamper: true,
      // SATEL-style 2EOL-with-resistors pattern doesn't apply → leave
      // builtIn2EOL undefined. dimensionsMm: Ø20 × 90 mm (round barrel).
      dimensionsMm: 'Ø20 × 90',
    },
  },

  // https://ajax.systems/products/specs/fireprotect-plus-jeweller/
  // Residential FireProtect Plus (1st-gen combo) — NOT the EN54 pro line
  // (that lives in ajaxFireCatalog.ts). Smoke (photoelectric) + heat
  // (thermistor) + CO (chemical sensor). EN 14604 standalone-alarm cert.
  {
    id: 'ajax-fireprotect-plus-jeweller',
    manufacturer: 'Ajax',
    model: 'FireProtect Plus Jeweller',
    category: 'smoke-detector',
    tagline: 'Wireless smoke + heat + CO detector, siren, IP40, ~4-yr battery',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/fireprotect-plus-jeweller/',
    specs: {
      // Photoelectric smoke + thermistor heat + chemical CO sensor.
      detection: ['smoke-optical', 'heat-fixed', 'co'],
      standard: ['EN 14604'], // EN 14604:2005/AC:2008; CO per EN 50291 (not in enum)
      standalone: false, // panel-connected (Jeweller hub)
      batteryYears: 4, // 2× CR2 pre-installed, up to 4 yr; CR2032 backup
      wirelessSystem: 'Jeweller',
    },
  },

  // https://ajax.systems/products/specs/fireprotect-2-rb-smoke-heat/
  // FireProtect 2 residential line (NOT EN54). RB = replaceable batteries.
  {
    id: 'ajax-fireprotect-2-rb-heat-smoke',
    manufacturer: 'Ajax',
    model: 'FireProtect 2 RB (Heat/Smoke) Jeweller',
    category: 'smoke-detector',
    tagline: 'Wireless dual-spectrum smoke + heat (A1R), 2× CR123A up to 7 yr, IP20',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/fireprotect-2-rb-smoke-heat/',
    specs: {
      // Double-spectrum optical smoke + heat (EN 54-5 class A1R, static + RoR).
      detection: ['smoke-optical', 'heat-fixed', 'heat-rate-of-rise'],
      standard: ['EN 14604', 'EN 54-5'],
      standalone: false, // panel-connected (Jeweller hub)
      batteryYears: 7, // 2× CR123A replaceable, up to 7 yr
      wirelessSystem: 'Jeweller',
    },
  },

  // https://ajax.systems/products/specs/fireprotect-2-sb-smoke-heat/
  // FireProtect 2 residential line (NOT EN54). SB = sealed non-replaceable
  // batteries, 10-yr life (replace whole detector at end of life).
  {
    id: 'ajax-fireprotect-2-sb-heat-smoke',
    manufacturer: 'Ajax',
    model: 'FireProtect 2 SB (Heat/Smoke) Jeweller',
    category: 'smoke-detector',
    tagline: 'Wireless dual-spectrum smoke + heat (A1R), sealed 10-yr battery, IP20',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/fireprotect-2-sb-smoke-heat/',
    specs: {
      detection: ['smoke-optical', 'heat-fixed', 'heat-rate-of-rise'],
      standard: ['EN 14604', 'EN 54-5'],
      standalone: false, // panel-connected (Jeweller hub)
      batteryYears: 10, // 2 sealed non-replaceable lithium cells, 10-yr life
      wirelessSystem: 'Jeweller',
    },
  },

  // https://ajax.systems/products/specs/fireprotect-2-rb-smoke-heat-co/
  // FireProtect 2 residential line (NOT EN54). RB = replaceable batteries.
  // Adds a chemical CO sensor (EN 50291) to the Heat/Smoke variant.
  {
    id: 'ajax-fireprotect-2-rb-heat-smoke-co',
    manufacturer: 'Ajax',
    model: 'FireProtect 2 RB (Heat/Smoke/CO) Jeweller',
    category: 'smoke-detector',
    tagline: 'Wireless smoke + heat (A1R) + CO, 2× CR123A up to 7 yr, IP20',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/fireprotect-2-rb-smoke-heat-co/',
    specs: {
      // Double-spectrum optical smoke + heat (A1R) + chemical CO sensor.
      detection: ['smoke-optical', 'heat-fixed', 'heat-rate-of-rise', 'co'],
      standard: ['EN 14604'], // + EN 54-5 (heat) + EN 50291-1:2018 (CO, not in enum)
      standalone: false, // panel-connected (Jeweller hub)
      batteryYears: 7, // 2× CR123A replaceable, up to 7 yr
      wirelessSystem: 'Jeweller',
    },
  },

  // https://ajax.systems/products/specs/fireprotect-2-sb-smoke-heat-co/
  // FireProtect 2 residential line (NOT EN54). SB = sealed non-replaceable
  // batteries, 10-yr life. Adds chemical CO sensor (EN 50291).
  {
    id: 'ajax-fireprotect-2-sb-heat-smoke-co',
    manufacturer: 'Ajax',
    model: 'FireProtect 2 SB (Heat/Smoke/CO) Jeweller',
    category: 'smoke-detector',
    tagline: 'Wireless smoke + heat (A1R) + CO, sealed 10-yr battery, IP20',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/fireprotect-2-sb-smoke-heat-co/',
    specs: {
      detection: ['smoke-optical', 'heat-fixed', 'heat-rate-of-rise', 'co'],
      standard: ['EN 14604', 'EN 54-5'], // + EN 50291-1:2018 for CO (not in enum)
      standalone: false, // panel-connected (Jeweller hub)
      batteryYears: 10, // 2 sealed non-replaceable lithium cells, 10-yr life
      wirelessSystem: 'Jeweller',
    },
  },

  // https://ajax.systems/products/specs/superior-motionprotect-jeweller/
  // Superior line = project-grade rebrand of the former "MotionProtect S Jeweller".
  {
    id: 'ajax-superior-motionprotect-jeweller',
    manufacturer: 'Ajax',
    model: 'Superior MotionProtect Jeweller',
    pattern: 'cone',
    category: 'indoor-ceiling-pir', // wall-mount indoor PIR; reuses indoor category
    rangeM: 12, // "up to 39 ft" @ 7.9 ft mount → 12 m / 2.4 m
    hfovDeg: 88.5,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.4,
    mountingHeightMaxM: 2.5,
    mounting: ['wall'],
    detectionMethod: 'pir', // PIR only, SmartDetect algorithm
    petImmune: true, // up to 44 lb / 1.6 ft → ~20 kg / 50 cm
    ipRating: 'IP50',
    batteryOperated: true, // 1× CR123A, up to 5 yrs
    tagline: 'Project-grade indoor PIR, 12 m / 88.5°, pet-immune 20 kg, IP50',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-motionprotect-jeweller/',
  },

  // https://ajax.systems/products/specs/superior-motionprotect-plus-jeweller/
  // Superior line = rebrand of the former "MotionProtect S Plus Jeweller".
  {
    id: 'ajax-superior-motionprotect-plus-jeweller',
    manufacturer: 'Ajax',
    model: 'Superior MotionProtect Plus Jeweller',
    pattern: 'cone',
    category: 'indoor-ceiling-pir',
    rangeM: 12, // "up to 39 ft" @ 7.9 ft mount → 12 m / 2.4 m
    hfovDeg: 88.5,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.4,
    mountingHeightMaxM: 2.5,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual', // PIR + K-band (24 GHz) microwave
    petImmune: true, // up to 44 lb / 1.6 ft → ~20 kg
    // ⚠ anti-masking NOT listed on the spec page for the Plus (only base Outdoor
    //   models publish it). Removed to avoid asserting an unconfirmed feature.
    ipRating: 'IP50',
    batteryOperated: true, // 1× CR123A, up to 5 yrs
    tagline: 'Indoor PIR + 24 GHz microwave dual-tech, IP50, pet-immune 20 kg',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-motionprotect-plus-jeweller/',
  },

  // https://ajax.systems/products/specs/superior-motioncam-phod-jeweller/
  // Superior line = rebrand of the former "MotionCam S (PhOD) Jeweller".
  // Built-in camera for photo-on-demand verification; still a PIR FOV cone.
  {
    id: 'ajax-superior-motioncam-phod-jeweller',
    manufacturer: 'Ajax',
    model: 'Superior MotionCam (PhOD) Jeweller',
    pattern: 'cone',
    category: 'indoor-ceiling-pir',
    rangeM: 12, // "up to 39 ft" @ 7.9 ft mount → 12 m / 2.4 m
    hfovDeg: 88.5,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.4,
    mountingHeightMaxM: 2.5,
    mounting: ['wall'],
    detectionMethod: 'pir', // PIR + verification camera (photo-on-demand)
    petImmune: true, // up to 44 lb / 1.6 ft → ~20 kg
    ipRating: 'IP50',
    batteryOperated: true, // 2× CR123A, up to 4 yrs (3 yrs with PhOD on)
    tagline: 'Indoor PIR with photo-verification camera, 12 m / 88.5°, IP50',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-motioncam-phod-jeweller/',
  },

  // https://ajax.systems/products/specs/superior-combiprotect-jeweller/
  // Superior line = rebrand of the former "CombiProtect S Jeweller".
  // Combined PIR motion + acoustic glass-break. Modelled as a PIR cone
  // (motion is the placeable FOV); glass-break range noted in the tagline.
  {
    id: 'ajax-superior-combiprotect-jeweller',
    manufacturer: 'Ajax',
    model: 'Superior CombiProtect Jeweller',
    pattern: 'cone',
    category: 'indoor-ceiling-pir',
    rangeM: 12, // motion up to 12 m; glass-break to 9 m
    hfovDeg: 88.5,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.4,
    mountingHeightMaxM: 2.5,
    mounting: ['wall'],
    detectionMethod: 'pir', // PIR motion + electret-mic glass-break
    petImmune: true, // up to 44 lb / 1.6 ft → ~20 kg
    ipRating: 'IP50',
    batteryOperated: true, // 1× CR123A, up to 5 yrs
    tagline: 'Combined PIR (12 m/88.5°) + glass-break (9 m) detector, IP50',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-combiprotect-jeweller/',
  },

  // https://ajax.systems/products/specs/superior-doorprotect-jeweller/
  // Superior line = rebrand of the former "DoorProtect S Jeweller".
  {
    id: 'ajax-superior-doorprotect-jeweller',
    manufacturer: 'Ajax',
    model: 'Superior DoorProtect Jeweller',
    category: 'magnetic-contact',
    tagline: 'Project-grade wireless reed contact, wired NC input, 7-yr battery',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-doorprotect-jeweller/',
    specs: {
      mountType: 'surface', // SmartBracket panel
      housing: 'plastic', // black or white
      tamper: true, // detach notifications
      // 2 reed switches (small + large magnet) + wired NC 3rd-party input.
    },
  },

  // https://ajax.systems/products/specs/superior-doorprotect-plus-jeweller/
  // Superior line = rebrand of the former "DoorProtect S Plus Jeweller".
  // Reed contact + accelerometer (shock + tilt) in one detector.
  {
    id: 'ajax-superior-doorprotect-plus-jeweller',
    manufacturer: 'Ajax',
    model: 'Superior DoorProtect Plus Jeweller',
    category: 'magnetic-contact',
    tagline: 'Wireless reed contact + shock/tilt accelerometer, 5-yr battery',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-doorprotect-plus-jeweller/',
    specs: {
      mountType: 'surface', // SmartBracket panel
      housing: 'plastic', // black or white
      tamper: true, // detach notifications + shock/tilt accelerometer
      // 2 reed switches + accelerometer (3-level shock, 5°–25° tilt).
    },
  },

  // https://ajax.systems/products/specs/superior-glassprotect-jeweller/
  // Superior line = rebrand of the former "GlassProtect S Jeweller".
  {
    id: 'ajax-superior-glassprotect-jeweller',
    manufacturer: 'Ajax',
    model: 'Superior GlassProtect Jeweller',
    category: 'glass-break',
    tagline: 'Acoustic DualTone glass-break, 9 m / 180°, 7-yr battery',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-glassprotect-jeweller/',
    specs: {
      method: 'acoustic-dual', // DualTone: low- + high-frequency analysis
      // Recognises standard pane, insulated (double) glazing, and tempered
      // glass. Ajax does not claim laminated/film-covered glass.
      glassTypes: ['plain', 'tempered'],
      rangeM: 9, // up to 29.5 ft → 9 m
    },
  },

  // https://ajax.systems/products/specs/motionprotect-fibra/
  {
    id: 'ajax-motionprotect-fibra',
    manufacturer: 'Ajax',
    model: 'Superior MotionProtect Fibra',
    pattern: 'cone',
    category: 'indoor-ceiling-pir', // wall-mount indoor PIR; reuses indoor category
    rangeM: 12,
    hfovDeg: 88.5,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.4,
    mountingHeightMaxM: 2.5,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true, // up to 20 kg / 0.5 m per Ajax spec page
    ipRating: 'IP50',
    batteryOperated: false, // wired — powered from the Fibra bus (5–30 V, up to 7.5 mA)
    tagline: 'Wired Fibra-bus indoor PIR, 12 m / 88.5°, pet-immune to 20 kg',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/motionprotect-fibra/',
  },

  // https://ajax.systems/products/specs/motionprotect-plus-fibra/
  {
    id: 'ajax-motionprotect-plus-fibra',
    manufacturer: 'Ajax',
    model: 'Superior MotionProtect Plus Fibra',
    pattern: 'cone',
    category: 'indoor-ceiling-pir',
    rangeM: 12,
    hfovDeg: 88.5,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.4,
    mountingHeightMaxM: 2.4,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual', // PIR + K-band (24 GHz) microwave
    petImmune: true, // up to 20 kg / 0.5 m per Ajax spec page
    ipRating: 'IP50',
    batteryOperated: false, // wired — Fibra bus (5–30 V, up to 187 mW)
    tagline: 'Wired Fibra PIR + K-band microwave dual-tech, pet-immune to 20 kg',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/motionprotect-plus-fibra/',
  },

  // https://ajax.systems/products/specs/motioncam-fibra/
  {
    id: 'ajax-motioncam-fibra',
    manufacturer: 'Ajax',
    model: 'Superior MotionCam Fibra',
    pattern: 'cone',
    category: 'indoor-ceiling-pir',
    rangeM: 12,
    hfovDeg: 88.5,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.4,
    mountingHeightMaxM: 2.4,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true, // up to 20 kg / 0.5 m per Ajax spec page
    ipRating: 'IP50',
    batteryOperated: false, // wired — Fibra bus (7–30 V) + LTO AA aux cell for the camera
    tagline: 'Wired Fibra PIR with photo-verification camera, 12 m / 88.5°',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/motioncam-fibra/',
  },

  // https://ajax.systems/products/specs/superior-motioncam-phod-fibra/
  {
    id: 'ajax-motioncam-phod-fibra',
    manufacturer: 'Ajax',
    model: 'Superior MotionCam (PhOD) Fibra',
    pattern: 'cone',
    category: 'indoor-ceiling-pir',
    rangeM: 12,
    hfovDeg: 88.5,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.4,
    mountingHeightMaxM: 2.4,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true, // up to 20 kg / 0.5 m per Ajax spec page
    ipRating: 'IP50',
    batteryOperated: false, // wired — Fibra bus + LTO AA aux cell (photo-on-demand + scenario photos)
    tagline: 'Wired Fibra PIR, photo-on-demand + scenario photos, 12 m / 88.5°',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-motioncam-phod-fibra/',
  },

  // https://ajax.systems/products/specs/superior-motionprotect-g3-fibra/
  {
    id: 'ajax-motionprotect-g3-fibra',
    manufacturer: 'Ajax',
    model: 'Superior MotionProtect G3 Fibra',
    pattern: 'cone',
    category: 'indoor-ceiling-pir',
    rangeM: 15,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.4,
    mountingHeightMaxM: 2.4,
    mounting: ['wall'],
    detectionMethod: 'pir',
    antiMasking: true, // advanced anti-masking, EN 50131 Grade 3
    petImmune: false, // Ajax: pet immunity NOT available on the G3 detector
    batteryOperated: false, // wired — Fibra bus (7–30 V)
    tagline: 'Grade 3 wired Fibra PIR with anti-masking, 15 m / 90°',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-motionprotect-g3-fibra/',
  },

  // https://ajax.systems/products/specs/superior-motionprotect-plus-g3-fibra/
  {
    id: 'ajax-motionprotect-plus-g3-fibra',
    manufacturer: 'Ajax',
    model: 'Superior MotionProtect Plus G3 Fibra',
    pattern: 'cone',
    category: 'indoor-ceiling-pir',
    rangeM: 15,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.4,
    mountingHeightMaxM: 2.4,
    mounting: ['wall'],
    detectionMethod: 'pir-mw-dual', // PIR + K-band (24 GHz) microwave
    antiMasking: true, // Grade 3 anti-masking
    petImmune: false, // Ajax: pet immunity NOT available on the Plus G3 detector
    batteryOperated: false, // wired — Fibra bus (7–30 V)
    tagline: 'Grade 3 wired Fibra PIR + K-band microwave, anti-masking, 15 m / 90°',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-motionprotect-plus-g3-fibra/',
  },

  // https://ajax.systems/products/specs/combiprotect-fibra/
  // Combined PIR motion + acoustic glass-break; modelled by its motion cone
  // (the geometry the designer draws). Glass-break noted in the tagline.
  {
    id: 'ajax-combiprotect-fibra',
    manufacturer: 'Ajax',
    model: 'Superior CombiProtect Fibra',
    pattern: 'cone',
    category: 'indoor-ceiling-pir',
    rangeM: 12,
    hfovDeg: 88.5,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 2.4,
    mountingHeightMaxM: 2.4,
    mounting: ['wall'],
    detectionMethod: 'pir', // PIR motion + acoustic DualTone glass-break (0.3–9 m, 180°)
    petImmune: true, // up to 20 kg / 0.5 m per Ajax spec page
    batteryOperated: false, // wired — Fibra bus (5–30 V, up to 7.7 mA)
    tagline: 'Wired Fibra PIR + DualTone glass-break, 12 m / 88.5°, glass to 9 m',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/combiprotect-fibra/',
  },

  // https://ajax.systems/products/specs/doorprotect-fibra/
  {
    id: 'ajax-doorprotect-fibra',
    manufacturer: 'Ajax',
    model: 'Superior DoorProtect Fibra',
    category: 'magnetic-contact',
    tagline: 'Wired Fibra-bus reed-switch door/window contact, external NC input',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/doorprotect-fibra/',
    specs: {
      mountType: 'surface',
      housing: 'plastic',
      tamper: true,
      // Supports one 3rd-party wired NC detector on its input; the SATEL-
      // specific 2EOL-with-resistors pattern doesn't apply → builtIn2EOL left off.
    },
  },

  // https://ajax.systems/products/specs/doorprotect-plus-fibra/
  {
    id: 'ajax-doorprotect-plus-fibra',
    manufacturer: 'Ajax',
    model: 'Superior DoorProtect Plus Fibra',
    category: 'magnetic-contact',
    tagline: 'Wired Fibra contact with shock + tilt sensor, IP50/IK04, NC input',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/doorprotect-plus-fibra/',
    specs: {
      mountType: 'surface',
      housing: 'plastic', // IP50 / IK04 plastic enclosure
      tamper: true,
      // Adds a 3-axis accelerometer (shock, 3 levels) + tilt sensor (5–25°)
      // beyond the 2× reed switch; plus one wired NC input for a 3rd-party
      // detector. Not captured by MagneticContactSpec fields → noted in tagline.
    },
  },

  // https://ajax.systems/products/specs/glassprotect-fibra/
  {
    id: 'ajax-glassprotect-fibra',
    manufacturer: 'Ajax',
    model: 'Superior GlassProtect Fibra',
    category: 'glass-break',
    tagline: 'Wired Fibra acoustic DualTone glass-break, 9 m / 180°',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/glassprotect-fibra/',
    specs: {
      method: 'acoustic-dual', // DualTone: low-freq impact + high-freq break
      // Ajax spec lists plain, insulated and tempered glass; laminated/film not stated.
      glassTypes: ['plain', 'tempered'],
      rangeM: 9,
    },
  },


  // ═══════════════════════════════════════════════════════════════════
  // AJAX: integration module (third-party wired device onto Jeweller)
  // ═══════════════════════════════════════════════════════════════════

  // https://ajax.systems/products/specs/transmitter-jeweller/
  // Cross-checked against the official TDS (EN, rev. 2026-03-03).
  // Category follows the EN54 I/O Module precedent in ajaxFireCatalog.ts:
  // a radio bridge between a wired device and the hub is a 'wireless-expander'.
  {
    id: 'ajax-transmitter-jeweller',
    manufacturer: 'Ajax',
    model: 'Transmitter Jeweller',
    category: 'wireless-expander',
    tagline:
      'Jeweller module bringing one third-party wired NC/NO device onto an Ajax hub: 3.3 V⎓ / 125 mA output for the detector, alarm + tamper terminals, 130 m cable (24 AWG), pulse or bistable, 14 event types, 3× CR123A, EN 50131 Grade 2',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/transmitter-jeweller/',
    specs: {
      frequency: '866.0–869.2 / 905.0–926.5 MHz (Jeweller, region-dependent)',
      biDirectional: true,
      // One wired device per module: the alarm terminal pair is single.
      zonesPerModule: 1,
      busConnection: 'Jeweller radio to hub or range extender',
      compatibility: ['all Ajax hubs', 'all Ajax radio signal range extenders'],
      rangeM: 1600,
    },
  },

  // ═══════════════════════════════════════════════════════════════════
  // AJAX — NVR
  // ═══════════════════════════════════════════════════════════════════

  // https://ajax.systems/products/specs/nvr-h2d8pac-8-ch/
  // "8 channels"; "incoming: up to 160 Mbps", outgoing the same; recording up
  // to "12 MP"; "2 × SATA III", "up to 16 TB"; "8 × IEEE 802.3af/at", "up to
  // 160 W" in total; H.265 / H.264; ONVIF Profile G and S cameras; 12.99 ×
  // 12.99 × 1.73 in (44 mm) with rack-mount brackets in the box.
  {
    id: 'ajax-nvr-h2d8pac-8ch',
    manufacturer: 'Ajax',
    model: 'NVR H2D8PAC (8-ch)',
    category: 'nvr-recorder',
    tagline: '8ch Ajax NVR, 12 MP, 8× PoE (160 W), 160 Mbps, 2 HDD',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/nvr-h2d8pac-8-ch/',
    specs: {
      channels: 8,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 160,
      maxResolution: '12 MP',
      hddBays: 2,
      hddBayMaxTb: 16,
      poeInPorts: 8,
      poeBudgetW: 160,
      codecs: ['H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
    },
  },

  // https://ajax.systems/products/specs/superior-nvr-h2dai2gac-8-ch/
  // "8 channels"; recording up to "12 MP"; "H.265, H.264"; "2 × SATA III:
  // supports drives with up to 24 TB capacity"; "2 × RJ-45 (WAN and LAN)
  // self-adaptive Ethernet (10/100/1000 Mbps)", no PoE ports; bandwidth
  // "incoming: up to 384 Mbps", "outgoing: up to 384 Mbps"; ONVIF cameras;
  // AI object detection on "up to 8 channels"; "Power consumption: up to
  // 60 W"; 12.99″ × 12.99″ × 1.73″ (44 mm), "2 × rack-mount brackets" in the
  // box; indoor only, IP20.
  {
    id: 'ajax-superior-nvr-h2dai2gac-8ch',
    manufacturer: 'Ajax',
    model: 'Superior NVR H2DAI2GAC (8-ch)',
    category: 'nvr-recorder',
    tagline: '8ch Ajax AI NVR, 12 MP, 384 Mbps, 2 HDD up to 24 TB, no PoE',
    verified: true,
    datasheetUrl: 'https://ajax.systems/products/specs/superior-nvr-h2dai2gac-8-ch/',
    specs: {
      channels: 8,
      inputBandwidthMbps: 384,
      outputBandwidthMbps: 384,
      maxResolution: '12 MP',
      hddBays: 2,
      hddBayMaxTb: 24,
      poeInPorts: 0,
      codecs: ['H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '2× RJ-45 1 GbE (WAN, LAN)',
    },
  },

  // ═══════════════════════════════════════════════════════════════════
  // HANWHA — Wisenet NVRs
  // ═══════════════════════════════════════════════════════════════════

  // https://www.hanwhavisionamerica.com/product/qrn-820s/
  {
    id: 'hanwha-qrn-820s',
    manufacturer: 'Hanwha Vision',
    model: 'QRN-820S',
    category: 'nvr-recorder',
    tagline: '8ch Wisenet Q-series NVR, 8 MP, 8× PoE+ (65 W), 80 Mbps',
    verified: true,
    datasheetUrl: 'https://www.hanwhavisionamerica.com/product/qrn-820s/',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      maxResolution: '8 MP',
      hddBays: 1,
      hddBayMaxTb: 6,
      poeInPorts: 8,
      poeBudgetW: 65,
      codecs: ['H.264', 'H.265', 'MJPEG'],
      onvif: true,
      formFactor: '1U', // 47.1 mm chassis
    },
  },

  // https://www.hanwhavisionamerica.com/product/prn-3200b2/
  {
    id: 'hanwha-prn-3200b2',
    manufacturer: 'Hanwha Vision',
    model: 'PRN-3200B2',
    category: 'nvr-recorder',
    tagline: '32ch P-series enterprise NVR, 32 MP, 400 Mbps, RAID 5/6, 80 TB',
    verified: true,
    datasheetUrl: 'https://www.hanwhavisionamerica.com/product/prn-3200b2/',
    specs: {
      channels: 32,
      inputBandwidthMbps: 400,
      outputBandwidthMbps: 64,
      maxResolution: '32 MP',
      hddBays: 8,
      hddBayMaxTb: 10,
      codecs: ['H.264', 'H.265', 'MJPEG'],
      raidLevels: ['5', '6'],
      onvif: true,
      formFactor: '2U', // 438 × 86 × 434.9 mm → 86 mm = 2U
      networkPorts: '3× RJ-45 1G',
    },
  },

  // https://www.hanwhavisionamerica.com/product/xrn-3210b4/
  {
    id: 'hanwha-xrn-3210b4',
    manufacturer: 'Hanwha Vision',
    model: 'XRN-3210B4',
    category: 'nvr-recorder',
    tagline: '32ch X-series AI NVR, 32 MP, 400 Mbps, 16 HDD, RAID 5/6',
    verified: true,
    datasheetUrl: 'https://www.hanwhavisionamerica.com/product/xrn-3210b4/',
    specs: {
      channels: 32,
      inputBandwidthMbps: 400,
      maxResolution: '32 MP',
      hddBays: 16,
      hddBayMaxTb: 10,
      codecs: ['H.264', 'H.265', 'MJPEG'],
      raidLevels: ['5', '6'],
      onvif: true,
      formFactor: '3U', // 440 × 132 × 571 mm → 132 mm = 3U
    },
  },

  // https://www.hanwhavisionamerica.com/product/xrn-6410b2/
  {
    id: 'hanwha-xrn-6410b2',
    manufacturer: 'Hanwha Vision',
    model: 'XRN-6410B2',
    category: 'nvr-recorder',
    tagline: '64ch X-series NVR, 32 MP, 400 Mbps, 8 HDD, N+1 failover',
    verified: true,
    datasheetUrl: 'https://www.hanwhavisionamerica.com/product/xrn-6410b2/',
    specs: {
      channels: 64,
      inputBandwidthMbps: 400,
      outputBandwidthMbps: 32,
      maxResolution: '32 MP',
      hddBays: 8,
      hddBayMaxTb: 10,
      codecs: ['H.264', 'H.265', 'MJPEG'],
      onvif: true,
      formFactor: '2U', // 438 × 86 × 434.9 mm
      networkPorts: '3× RJ-45 1G',
    },
  },

  // https://www.hanwhavisionamerica.com/product/wrn-1610s/
  {
    id: 'hanwha-wrn-1610s',
    manufacturer: 'Hanwha Vision',
    model: 'WRN-1610S',
    category: 'nvr-recorder',
    tagline: '16ch WAVE PoE+ NVR, 16× PoE+ (200 W), 4 HDD (24 TB), Ubuntu',
    verified: true,
    datasheetUrl: 'https://www.hanwhavisionamerica.com/product/wrn-1610s/',
    specs: {
      channels: 16,
      hddBays: 4,
      hddBayMaxTb: 6, // 24 TB total / 4 bays
      poeInPorts: 16,
      poeBudgetW: 200,
      codecs: ['H.264', 'H.265', 'MJPEG'],
      onvif: true,
    },
  },

  // https://www.hanwhavisionamerica.com/product/hrx-1632/
  {
    id: 'hanwha-hrx-1632',
    manufacturer: 'Hanwha Vision',
    model: 'HRX-1632',
    category: 'nvr-recorder',
    tagline: '16-analog + 8-IP pentabrid recorder, AHD/TVI/CVI/CVBS/IP, 80 Mbps',
    verified: true,
    datasheetUrl: 'https://www.hanwhavisionamerica.com/product/hrx-1632/',
    specs: {
      channels: 24, // 16 analog + 8 IP
      inputBandwidthMbps: 80,
      maxResolution: '8 MP', // IP channels reach 8 MP
      hddBays: 4,
      hddBayMaxTb: 6,
      codecs: ['H.264', 'H.265', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      hybrid: true,
      alarmIO: '16 in / 4 out',
    },
  },
];
